/**
 * Strategem projectile / overlay FX: keep battlefield HP at pre-hit values until damage pop + countdown.
 */

export function shouldHoldHealthForStrategemAnimation(anim) {
  if (!anim || anim.targetType !== 'unit' || anim.effectType === 'heal') {
    return false;
  }
  const ghost = anim.ghostUnit;
  if (!ghost?.instanceId || typeof ghost.health !== 'number' || ghost.health <= 0) {
    return false;
  }
  return true;
}

/**
 * Attach pre/post HP metadata at strategem registration (before two-phase effect applies).
 */
export function enrichStrategemUnitDamageMeta(anim, damageAmount) {
  if (!shouldHoldHealthForStrategemAnimation(anim)) {
    return anim;
  }
  const healthBefore = anim.healthBefore ?? anim.ghostUnit.health;
  const targetInstanceId = anim.targetInstanceId ?? anim.ghostUnit.instanceId;
  if (typeof damageAmount !== 'number' || damageAmount <= 0) {
    return { ...anim, healthBefore, targetInstanceId };
  }
  return {
    ...anim,
    targetInstanceId,
    healthBefore,
    healthAfter: Math.max(0, healthBefore - damageAmount),
    damageAmount,
  };
}

/**
 * Resolve post-hit HP for survivor countdown when server state may still be pre-hit at projectile impact.
 */
export function resolveSurvivorHealthAfter({
  healthBefore,
  healthAfter,
  damageAmount,
  liveHealth,
}) {
  let resolvedAfter = healthAfter;
  if (
    typeof liveHealth === 'number'
    && typeof healthBefore === 'number'
    && liveHealth < healthBefore
  ) {
    resolvedAfter = liveHealth;
  } else if (resolvedAfter == null && typeof liveHealth === 'number') {
    resolvedAfter = liveHealth;
  }
  if (
    typeof healthBefore === 'number'
    && typeof damageAmount === 'number'
    && damageAmount > 0
    && (resolvedAfter == null || resolvedAfter >= healthBefore)
  ) {
    resolvedAfter = Math.max(0, healthBefore - damageAmount);
  }
  return resolvedAfter;
}

function getLiveUnitHealthAtSlot(gameState, viewerUserId, opponentId, zoneName, index, targetInstanceId) {
  if (!gameState?.state || zoneName == null || index == null || !targetInstanceId) {
    return undefined;
  }
  const zone = zoneName.includes('_front') ? 'front' : zoneName.includes('_flank') ? 'flank' : null;
  if (!zone) {
    return undefined;
  }
  const playerId = zoneName.startsWith('your_') ? viewerUserId : opponentId;
  if (!playerId) {
    return undefined;
  }
  const unit = gameState.state[playerId]?.playedOrders?.[zone]?.[index];
  if (unit?.instanceId === targetInstanceId && typeof unit.health === 'number') {
    return unit.health;
  }
  if (!unit || unit.instanceId !== targetInstanceId) {
    return 0;
  }
  return undefined;
}

/**
 * Authoritative post-hit HP for survivor countdown (uses live slot state when available).
 */
export function resolveCountdownTargetHealth(trigger, gameState, viewerUserId, opponentId) {
  if (!trigger?.targetInstanceId || typeof trigger.healthBefore !== 'number') {
    return trigger?.healthAfter;
  }

  let liveHealth;
  const { targetZone, targetIndex } = trigger;
  if (targetZone != null && targetIndex != null) {
    liveHealth = getLiveUnitHealthAtSlot(
      gameState,
      viewerUserId,
      opponentId,
      targetZone,
      targetIndex,
      trigger.targetInstanceId
    );
  }

  return resolveSurvivorHealthAfter({
    healthBefore: trigger.healthBefore,
    healthAfter: typeof trigger.healthAfter === 'number' ? trigger.healthAfter : undefined,
    damageAmount: typeof trigger.damageAmount === 'number' ? trigger.damageAmount : undefined,
    liveHealth,
  });
}

function mergeAuthoritativeDamageFields(actualOrder, displayUnit) {
  if (!actualOrder || !displayUnit) {
    return displayUnit;
  }
  return {
    ...displayUnit,
    maxHealth: actualOrder.maxHealth ?? displayUnit.maxHealth,
    damageTaken: actualOrder.damageTaken ?? displayUnit.damageTaken,
    preShrinkMaxHealth: actualOrder.preShrinkMaxHealth ?? displayUnit.preShrinkMaxHealth,
    _authoritativeHealth: actualOrder.health,
  };
}

/**
 * Prefer pre-hit ghost snapshot only while HP hold / countdown is active.
 * Otherwise show authoritative HP so stale ghost snapshots cannot inflate displayed health.
 */
export function pickBattlefieldUnitForDisplay(actualOrder, ghostUnit, options = {}) {
  const { preferGhostSnapshot = false } = options;
  if (!actualOrder) {
    return ghostUnit ?? null;
  }
  if (
    preferGhostSnapshot
    && ghostUnit
    && ghostUnit.instanceId
    && actualOrder.instanceId === ghostUnit.instanceId
    && typeof ghostUnit.health === 'number'
    && typeof actualOrder.health === 'number'
    && ghostUnit.health > actualOrder.health
  ) {
    return mergeAuthoritativeDamageFields(actualOrder, ghostUnit);
  }
  return actualOrder;
}
